"use client";

import React, { Component, JSX } from "react";
import Link from "next/link";
import styles from "./ContentInterpreter.module.scss";
import { dataidGenerator } from "@/lib/stringUtil";

const headerPattern = /h([1-6])/;

interface ContentInterpreterProps {
  parentTag?: React.ElementType;
  text?: string;
  tooltipCode?: string;
  linkCode?: string;
  highlightCode?: string;
  boldCode?: string;
  italicCode?: string;
  underlineCode?: string;
  newParagraphCode?: string;
  linebreakCode?: string;
  listCode?: string;
  renderAsPTags?: boolean;
  location?: Record<string, unknown>;
  className?: string;
  style?: React.CSSProperties; // Add style prop
}

interface Content {
  text: string;
  indexStart: number;
  indexEnd: number;
  id: number;
  nextIndexStart?: number;
  nextIndexEnd?: number;
}

class ContentInterpreter extends Component<ContentInterpreterProps> {
  static readonly defaultProps = {
    boldCode: "bold",
    underlineCode: "underline",
    italicCode: "italic",
    tooltipCode: "tt",
    linkCode: "link",
    highlightCode: "hl",
    parentTag: "div",
    newParagraphCode: "br",
    linebreakCode: "lb",
    listCode: "bullet",
    renderAsPTags: true,
  };

  validTags: (string | boolean)[] = [];

  constructor(props: ContentInterpreterProps) {
    super(props);
    this.validTags = Object.values(ContentInterpreter.defaultProps);
  }

  _getNextBlock(content: Content): (string | JSX.Element)[] {
    const nextTag = this._getNextTag(content);
    if (
      nextTag.length > 0 &&
      nextTag !== this.props.newParagraphCode &&
      nextTag !== this.props.listCode &&
      !headerPattern.test(nextTag)
    ) {
      const renderedContent = this._getInlineContent(nextTag, content, []);
      const OuterTag = this.props.renderAsPTags ? "p" : "span";
      return [
        <OuterTag key={this._getNextKey(content)}>{renderedContent}</OuterTag>,
      ];
    } else if (headerPattern.test(nextTag)) {
      return [this._renderHeaders(nextTag, content)];
    }
    if (nextTag === this.props.linebreakCode) {
      const contentArray: (string | JSX.Element)[] = [];
      this._renderLinebreak(content, contentArray);
      return contentArray;
    }
    if (nextTag === this.props.listCode) {
      return [this._renderList(nextTag, content)];
    }
    return [this._endParagraph(content)];
  }

  _getInlineContent(
    tag: string,
    content: Content,
    contentArray: (string | JSX.Element)[]
  ): (string | React.JSX.Element)[] {
    switch (tag) {
      case this.props.boldCode:
      case this.props.underlineCode:
      case this.props.italicCode: {
        this._renderBoldItalicUnderline(tag, content, contentArray);
        break;
      }
      case this.props.highlightCode: {
        this._renderInteractiveElements(content, contentArray);
        break;
      }
      case this.props.linebreakCode: {
        this._renderLinebreak(content, contentArray);
        break;
      }
      default: {
        const returnText = content.text;
        const ParentTag = this.props.renderAsPTags ? "p" : "span";
        this._setContentText(content, "");
        return returnText.length > 0
          ? [
              <ParentTag key={this._getNextKey(content)}>
                {returnText}
              </ParentTag>,
            ]
          : [];
      }
    }
    const nextTag = this._getNextTag(content, true);
    if (this._isInlineTag(nextTag)) {
      content.indexStart = content.nextIndexStart!;
      content.indexEnd = content.nextIndexEnd!;
      return this._getInlineContent(nextTag, content, contentArray);
    }
    const text = content.text.substring(0, content.nextIndexStart);
    if (text.length > 0) {
      contentArray.push(text);
    }
    this._setContentText(
      content,
      content.text.substring(
        nextTag === this.props.newParagraphCode
          ? content.nextIndexStart! + nextTag.length + 2
          : content.nextIndexStart!
      )
    );
    return contentArray;
  }

  _renderLinebreak(content: Content, contentArray: (string | JSX.Element)[]) {
    const text = content.text.substring(0, content.indexStart);
    this._setContentText(content, content.text.substring(content.indexEnd + 1));

    if (text.length) {
      contentArray.push(text);
    }
    contentArray.push(<br key={this._getNextKey(content)} />);

    // Ensure the remaining content is processed
    if (content.text.length > 0) {
      content.indexStart = -1;
      content.indexEnd = -1;
    }
  }

  _renderHeaders(Tag: string, content: Content) {
    const headerText = this._getTextBetweenTags(Tag, content);
    this._setContentText(
      content,
      content.text.substring(content.indexEnd + Tag.length + 3)
    );
    return headerText.length ? (
      <div key={this._getNextKey(content)} className={styles.header}>
        {headerText}
      </div>
    ) : (
      ""
    );
  }

  _renderInteractiveElements(
    content: Content,
    contentArray: (string | JSX.Element)[]
  ) {
    const { highlightCode = "", tooltipCode, linkCode } = this.props;
    const highlightWord = this._getTextBetweenTags(highlightCode, content);
    if (highlightWord.length > 0) {
      const text = content.text.substring(0, content.indexStart);
      const type = this._getNextTag(content);
      this._setContentText(content, content.text.substring(content.indexStart));
      switch (type) {
        case tooltipCode: {
          if (text.length) {
            contentArray.push(text);
          }
          contentArray.push(this._renderTooltip(highlightWord, content));
          break;
        }
        case linkCode: {
          if (text.length) {
            contentArray.push(text);
          }
          contentArray.push(this._renderLink(highlightWord, content));
          break;
        }
      }
    } else if (content.text.length > 0) {
      const returnText = content.text;
      this._setContentText(content, "");
      return returnText;
    }
    return null;
  }

  _renderLink(highlightedWord: string, content: Content) {
    const { linkCode = "", className, style } = this.props;
    const link = this._getTextBetweenTags(linkCode, content, true);

    // Extract text color from style prop for links
    const linkStyle = style ? { color: style.color } : undefined;

    if (link.startsWith("http")) {
      return (
        <a
          key={this._getNextKey(content)}
          className={`link ${className ?? ""}`}
          data-id={`${dataidGenerator(highlightedWord)}-link`}
          href={link}
          rel="noopener noreferrer"
          target="_blank"
          style={linkStyle}
        >
          {highlightedWord}
        </a>
      );
    }
    return (
      <Link
        key={this._getNextKey(content)}
        className={`link ${className || ""}`}
        data-id={`${dataidGenerator(highlightedWord)}-link`}
        href={link}
        legacyBehavior
      >
        <a style={linkStyle}>{highlightedWord}</a>
      </Link>
    );
  }

  _renderTooltip(highlightedWord: string, content: Content) {
    const { tooltipCode = "", className } = this.props;
    const tooltipText = this._getTextBetweenTags(tooltipCode, content, true);
    return <div>{`THERE SHOULD BE A TOOLTIP HERE with ${tooltipText}`}</div>;
  }

  _endParagraph(content: Content) {
    if (content.indexStart === -1 && content.indexEnd === -1) {
      content.indexStart = content.text.length;
      content.indexEnd = content.indexStart;
    }
    const { renderAsPTags } = this.props;
    const text = content.text.substring(0, content.indexStart);
    this._setContentText(content, content.text.substring(content.indexEnd + 1));
    const ParentTag = renderAsPTags ? "p" : "span";
    return text.length ? (
      <ParentTag key={this._getNextKey(content)}>{text}</ParentTag>
    ) : (
      ""
    );
  }

  _renderBoldItalicUnderline(
    tag: string,
    content: Content,
    contentArray: (string | JSX.Element)[]
  ) {
    const styledText = this._getTextBetweenTags(tag, content);
    const text = content.text.substring(0, content.indexStart);
    this._setContentText(
      content,
      content.text.substring(content.indexEnd + tag.length + 3)
    );
    const { boldCode, underlineCode, italicCode } = this.props;
    let className = "";

    switch (tag) {
      case boldCode: {
        className = styles.textWeightBold;
        break;
      }
      case italicCode: {
        className = styles.italic;
        break;
      }
      case underlineCode: {
        className = styles.underline;
        break;
      }
    }

    if (text.length) {
      contentArray.push(text);
    }

    contentArray.push(
      <span key={this._getNextKey(content)} className={className}>
        {styledText}
      </span>
    );
  }

  _renderList(tag: string, content: Content) {
    const list = this._getTextBetweenTags(tag, content).split("|");
    this._setContentText(
      content,
      content.text.substring(content.indexEnd + tag.length + 3)
    );
    return (
      <ul key={this._getNextKey(content)} className={styles.bulletList}>
        {list.map((item) => (
          <li key={this._getNextKey(content)}>{item}</li>
        ))}
      </ul>
    );
  }

  _getTextBetweenTags(tag: string, content: Content, trimText = false) {
    content.indexStart = content.text.indexOf(`<${tag}>`);
    content.indexEnd =
      content.indexStart > -1
        ? content.text.indexOf(`</${tag}>`, content.indexStart + 1)
        : -1;
    if (content.indexStart !== -1 && content.indexEnd !== -1) {
      const returnText = content.text.substring(
        content.indexStart + tag.length + 2,
        content.indexEnd
      );
      if (trimText) {
        this._setContentText(
          content,
          content.text.substring(content.indexEnd + tag.length + 3)
        );
      }
      return returnText;
    }
    return "";
  }

  _isInlineTag(tag: string) {
    switch (tag) {
      case this.props.boldCode:
      case this.props.underlineCode:
      case this.props.italicCode:
      case this.props.highlightCode:
      case this.props.linebreakCode: {
        return true;
      }
      default:
        return false;
    }
  }

  _isTagValid(tag: string) {
    return this.validTags.indexOf(tag) !== -1 || headerPattern.test(tag);
  }

  _getNextTag(content: Content, peek = false) {
    let { indexStart, indexEnd } = content;
    let tag = "";
    const { text } = content;
    let position = -1;

    do {
      indexStart = text.indexOf(
        "<",
        indexStart > indexEnd ? indexStart + 1 : indexEnd + 1
      );
      indexEnd = text.indexOf(">", indexStart + 1);
      if (position === -1) {
        position = indexStart > indexEnd ? indexEnd : indexStart;
      } else if (indexStart < position || indexEnd < position) {
        indexStart = position + 1;
        indexEnd = position + 1;
      } else {
        position = indexStart > indexEnd ? indexStart : indexEnd;
      }
      tag =
        indexEnd > indexStart ? text.substring(indexStart + 1, indexEnd) : "";

      // Normalize <br> tags
      if (tag === "br") {
        tag = this.props.linebreakCode!;
      }
    } while ((indexStart !== -1 || indexEnd !== -1) && !this._isTagValid(tag));
    if (tag.length === 0) {
      indexStart = -1;
      indexEnd = -1;
    }

    if (peek) {
      content.nextIndexStart = indexStart;
      content.nextIndexEnd = indexEnd;
    } else {
      content.indexEnd = indexEnd;
      content.indexStart = indexStart;
    }
    return tag;
  }

  _getNextKey(content: Content) {
    const key = `h${content.id}`;
    content.id += 1;
    return key;
  }

  _setContentText(content: Content, newText: string) {
    content.text = newText;
    content.indexStart = -1;
    content.indexEnd = -1;
  }

  render() {
    const {
      newParagraphCode,
      listCode,
      parentTag: ParentTag = "div",
      className,
      style, // Add style here
    } = this.props as ContentInterpreterProps;
    let { text } = this.props;
    const content: Content = {
      text: "",
      indexStart: -1,
      indexEnd: -1,
      id: 1,
    };
    const output: (string | JSX.Element)[] = [];
    let nextComp;
    const validTags: string[] = [];
    const endTags = [
      "<h1>",
      "<h2>",
      "<h3>",
      "<h4>",
      "<h5>",
      "<h6>",
      `<${listCode}>`,
    ];

    const keys = Object.keys(this.props);
    for (const element of keys) {
      if (element.endsWith("Code")) {
        validTags.push(
          this.props[element as keyof ContentInterpreterProps] as string
        );
      }
    }
    this.validTags = validTags;
    const newP = `<${newParagraphCode}>`;
    if (text) {
      if (!text.endsWith(newP)) {
        text += newP;
      }
      for (const element of endTags) {
        text = text.replace(new RegExp(`(?<!<br>)${element}`, "g"), `<br>${element}`);
      }
      text = text.replace(/(<br>)+/g, "<br>");
      content.text = text;
    }

    nextComp = this._getNextBlock(content);
    while (nextComp.length > 0) {
      console.log(`nextComp length: ${nextComp.length}`);
      console.log(`output length before push: ${output.length}`);
      console.log(`content: ${JSON.stringify(content)}`);
      if (output.length + nextComp.length > Number.MAX_SAFE_INTEGER) {
        throw new RangeError("Exceeded maximum array length");
      }
      output.push(...nextComp);
      console.log(`output length after push: ${output.length}`);
      nextComp = this._getNextBlock(content);

      // Ensure the loop continues if there is still text to process
      if (content.text.trim() === "") break;
    }
    return output.length === 0 ? null : (
      <ParentTag className={className || undefined} style={style}>{output}</ParentTag>
    );
  }
}

export default ContentInterpreter;
